import { FaUser } from 'react-icons/fa';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import { toastSuccess, toastError } from '../utils/toasts';
import EmptyTable from './Table/EmptyTable';
import FilterTable, { BaseData } from './Table/Table';
import Button from './atoms/Button';
import CustomModal from './atoms/CustomModal';
import { WireRequestDTO } from '../models/wire';
import { formatCurrency } from '../utils/shared';
import { parseFriendlyDateAndHour } from '../utils/dates';
import CreateEditWire from './CreateEditWire';
import MenuDropdown from './atoms/MenuDropdown';
import useGetUserRole from '../hooks/useGetUserRole';
import { useDeleteTransferMutation, useDeleteWireRequestMutation, useDownloadWiresMutation } from '../store';
import ApproveOrder from './ApproveWireRequest';
import useGetParams from '../hooks/useGetParams';
import CoinsHand from './icons/CoinsHand';

interface WireTableProps {
  data: WireRequestDTO[];
  triggerModal?: boolean;
  superadmin?: boolean;
  maxToRequest: number;
}

interface BankAccountsWire extends BaseData {
  status: JSX.Element;
  requested_at: JSX.Element;
  transferred_at: JSX.Element | string;
  requested_by: JSX.Element | string;
  transferred_by: JSX.Element | string;
  amount: JSX.Element | string;
  description: JSX.Element | string;
  display_bank_account: JSX.Element | string;
}

const WireTable = ({
  data, triggerModal = false, maxToRequest, superadmin = false,
}: WireTableProps) => {
  const navigate = useNavigate();
  const userRole = superadmin ? 'ROOT' : useGetUserRole();
  const { businessId, eventId } = superadmin ? { businessId: undefined, eventId: undefined } : useGetParams();
  const [deleteWireRequest, resDeleteWireRequest] = useDeleteWireRequestMutation();
  const [deleteWireTransfer, resDeleteWireTransfer] = useDeleteTransferMutation();
  const [downloadWires, resDownloadWires] = useDownloadWiresMutation();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'create' | 'createApproved' | 'edit' | 'delete' | 'approve' | 'view' | null>(null);
  const [selectedRow, setSelectedRow] = useState<BankAccountsWire | null>(null);

  const config = [
    {
      id: 'status',
      label: 'Estado',
    },
    {
      id: 'requested_at',
      label: 'Solicitado',
    },
    ...(!superadmin ? [{
      id: 'transferred_at',
      label: 'Transferido',
    }] : []),
    {
      id: 'requested_by',
      label: 'Solicitado por',
    },
    ...(superadmin ? [{
      id: 'event',
      label: 'Evento',
    }] : []),
    ...(!superadmin ? [{
      id: 'transferred_by',
      label: 'Transferido por',
    }] : []),
    {
      id: 'display_bank_account',
      label: 'Cuenta destino',
    },
    {
      id: 'amount',
      label: 'Monto',
    },
    ...(!superadmin ? [{
      id: 'description',
      label: 'Observación',
    }] : []),
  ];

  const statusLabel = (status: string) => {
    if (status === 'pending') {
      return <p className='tag-warning'>Pendiente</p>;
    } if (status === 'transferred') {
      return <p className='tag-primary'>Transferido</p>;
    }
    return <p className='tag-warning'>Rechazado</p>;
  };

  const rows: BankAccountsWire[] = data.map((item) => ({
    id: item.id,
    event_id: item.event_id,
    real_status: item.status,
    status: statusLabel(item.status),
    display_bank_account: `${item.bank_account?.holder_name} - ${item.bank_account?.entity_name}`,
    event: item.event_id ? <p className='whitespace-nowrap'>{item.event?.name}</p> : '-',
    requested_at: <p>{parseFriendlyDateAndHour(item.created_at, true, true)}hs</p>,
    transferred_at: item.wire_transfer ? <p className='whitespace-nowrap'>{parseFriendlyDateAndHour(item.wire_transfer.created_at, true, true)}hs</p> : '-',
    requested_by: item.request_user?.username || '-',
    transferred_by: item.wire_transfer?.transfer_user?.username || '-',
    amount: formatCurrency(item.amount, 'no-decimals'),
    description: item.wire_transfer?.description || '-',
  }));

  const handleCreate = () => {
    setModalType('create');
    setShowModal(true);
  };

  const handleCreateApproved = () => {
    setModalType('createApproved');
    setShowModal(true);
  };

  const handleDownloadXlsx = () => {
    downloadWires({
      eventId: eventId || '',
      businessId: businessId || '',
    });
  };

  useEffect(() => {
    if (triggerModal) {
      handleCreate();
    }
  }, [triggerModal]);

  useEffect(() => {
    if (resDeleteWireRequest.isSuccess) {
      toastSuccess('Solicitud eliminada correctamente');
    }
  }, [resDeleteWireRequest.isSuccess]);

  useEffect(() => {
    if (resDownloadWires.isSuccess) {
      toastSuccess('Solicitudes descargadas correctamente');
    }
  }, [resDownloadWires.isSuccess]);

  const customButtons = [
    <Button variant='secondary' onClick={handleCreate}>
      <div className='flex flex-row gap-2 items-center'>
        <CoinsHand className='text-xl text-text-primary' />
        <p className='whitespace-nowrap hidden md:block'>Solicitar dinero</p>
      </div>
    </Button>,
    ...(!superadmin ? [<Button variant='secondary' onClick={handleDownloadXlsx} loading={resDownloadWires.isLoading}>
      <div className='flex flex-row gap-2 items-center'>
        <FiDownload className='text-text-primary text-xl' />
        <p className='whitespace-nowrap hidden md:block'>Descargar</p>
      </div>
    </Button>] : []),
    ...(userRole === 'ROOT' ? [
      <Button variant='secondary' onClick={handleCreateApproved}>
        <div className='flex flex-row gap-2 items-center'>
          <CoinsHand className='text-xl text-text-primary' />
          <p className='whitespace-nowrap hidden md:block'>Crear transferencia</p>
        </div>
      </Button>,
    ] : []),
  ];

  const handleEdit = (row: BankAccountsWire) => {
    setSelectedRow(row);
    setModalType('edit');
    setShowModal(true);
  };

  const handleDelete = (row: BankAccountsWire) => {
    setSelectedRow(row);
    setModalType('delete');
    setShowModal(true);
  };

  const handleApprove = (row: BankAccountsWire) => {
    setSelectedRow(row);
    setModalType('approve');
    setShowModal(true);
  };

  const handleView = (row: BankAccountsWire) => {
    setSelectedRow(row);
    setModalType('view');
    setShowModal(true);
  };

  const handleDownload = (row: BankAccountsWire) => {
    const url = data.find((wire) => wire.id === row.id)?.wire_transfer?.receipt_pdf_url || '';
    if (url) {
      window.open(url, '_blank');
    } else {
      toastError('No se encontró el comprobante para descargar');
    }
  };

  const actions = (row: BankAccountsWire) => {
    const rowMenuOptions = [
      ...(userRole === 'ROOT' && row.real_status === 'pending' ? [{
        label: 'Aprobar solicitud',
        onClick: () => { handleApprove(row); },
      }] : []),
      ...(row.real_status === 'pending' && !superadmin ? [{
        label: 'Editar solicitud',
        onClick: () => { handleEdit(row); },
      }] : []),
      ...(row.real_status === 'pending' ? [{
        label: 'Eliminar solicitud',
        onClick: () => { handleDelete(row); },
      }] : []),
      ...(row.real_status === 'transferred' ? [{
        label: 'Ver solicitud',
        onClick: () => { handleView(row); },
      }] : []),
      ...(row.real_status === 'transferred' ? [{
        label: 'Descargar comprobante',
        onClick: () => { handleDownload(row); },
      }] : []),
      ...(userRole === 'ROOT' && row.real_status === 'transferred' ? [{
        label: 'Eliminar transferencia',
        onClick: () => { handleDelete(row); },
      }] : []),
    ];

    return (
      <div>
        {!superadmin ? (
          <MenuDropdown
            data={row}
            options={rowMenuOptions}
          />
        ) : (
          <img
            src="/eye.svg"
            alt="Ver"
            className='cursor-pointer mx-auto'
            onClick={() => {
              const originalItem = data.find((item) => item.id === row.id);
              navigate(`/admin/${originalItem?.bank_account?.business_id}/events/${row.event_id}/balance`);
            }}
          />
        )}
      </div>
    );
  };

  const handleClickDelete = async () => {
    if (selectedRow?.id.includes('wt-')) {
      await deleteWireTransfer({
        eventId: selectedRow?.event_id.toString() || '',
        wireId: selectedRow?.id.toString().replace('wt-', '') || '',
      });
    } else {
      await deleteWireRequest({
        eventId: selectedRow?.event_id.toString() || '',
        businessId: businessId || undefined,
        wireId: selectedRow?.id.toString() || '',
      });
    }
    setShowModal(false);
  };

  const renderModalContent = () => {
    switch (modalType) {
      case 'create':
        return (
          <div>
            <h1 className='text-2xl font-bold'>Crear solicitud</h1>
            <p className='text-sm text-text-tertiary'>
              Por este medio debes cargar el importe y cuenta donde quieres el envío de dinero.
            </p>
            <CreateEditWire showModal={showModal} setShowModal={setShowModal} maxToRequest={maxToRequest} superadmin={superadmin} />
          </div>
        );
      case 'createApproved':
        return (
          <div>
            <h1 className='text-2xl font-bold'>Crear transferencia [ROOT]</h1>
            <p className='text-sm text-text-tertiary'>
              Por este medio debes cargar el importe y cuenta donde quieres el envío de dinero.
            </p>
            <ApproveOrder
              data={data}
              row={{
                id: '',
                event_id: '',
                status: <p>-</p>,
                requested_at: <p>-</p>,
                transferred_at: <p>-</p>,
                requested_by: <p>-</p>,
                transferred_by: <p>-</p>,
                amount: <p>-</p>,
                description: <p>-</p>,
              }}
              setShowModal={setShowModal}
              view={false}
              superadmin={superadmin}
              root
            />
          </div>
        );
      case 'edit':
        return (
          <div>
            <h1 className='text-2xl font-bold'>Editar solicitud</h1>
            <p className='text-sm text-text-tertiary'>
              Por este medio debes cargar el importe y cuenta donde quieres el envío de dinero.
            </p>
            <CreateEditWire
              showModal={showModal}
              setShowModal={setShowModal}
              wire={data.find((wire) => wire.id === selectedRow?.id)}
              maxToRequest={maxToRequest}
              superadmin={superadmin}
            />
          </div>
        );
      case 'delete':
        return (
          <div className='flex flex-col'>
            <h1 className='text-2xl font-bold'>Eliminar solicitud</h1>
            <p className='text-text-tertiary'>¿Eliminar solicitud?</p>
            <p className='text-text-tertiary mt-4'>Una vez eliminada desaparecerá la de la lista y no se podrá revertir.</p>
            <div className='flex flex-row gap-2 mt-4'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button variant='primary' loading={resDeleteWireRequest.isLoading || resDeleteWireTransfer.isLoading} onClick={handleClickDelete}>Eliminar</Button>
            </div>
          </div>
        );
      case 'approve':
      case 'view':
        if (!selectedRow) {
          return null;
        }
        return (
          <div>
            <h1 className='text-2xl font-bold'>{modalType === 'approve' ? 'Aprobar solicitud' : 'Ver solicitud'}</h1>
            <p className='text-sm text-text-tertiary'>Por este medio debes cargar el importe y cuenta donde quieres el envío de dinero.</p>
            <ApproveOrder
              data={data}
              row={selectedRow}
              setShowModal={setShowModal}
              view={modalType === 'view'}
              superadmin={superadmin}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {data.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={config}
          customButtons={!superadmin ? customButtons : undefined}
          actions={actions}
          tableId="wire-table"
        />
      ) : (
        <EmptyTable
          title="No hay solicitudes de dinero"
          info="No hay solicitudes de dinero pendientes"
          icon={FaUser}
          iconColor="text-secondary-500"
          button={!superadmin ? [{
            label: 'Solicitar dinero',
            onClick: handleCreate,
            reactIcon: <CoinsHand className='text-xl' />,
            variant: 'secondary' as const,
          }, ...(userRole === 'ROOT' ? [{
            label: 'Crear transferencia',
            onClick: handleCreateApproved,
            reactIcon: <CoinsHand className='text-xl' />,
            variant: 'secondary' as const,
          }] : [])] : undefined}
        />
      )}
      <CustomModal isOpen={showModal} onRequestClose={() => setShowModal(false)} hideCancelButton>
        {renderModalContent()}
      </CustomModal>
    </div>
  );
};

export default WireTable;
